/**
 * ToolNav
 *
 * The "Go to League Home Screen" / "Go to Draft Board" chips shown above the
 * header of every tool page. A small hairline pill row, not a button and not
 * a breadcrumb — there's no implied hierarchy of slashes, just quick jumps
 * back out.
 *
 * Usage:
 *   <ToolNav leagueId={id} />
 *   <ToolNav leagueId={id} parent={{ label: 'Go to Draft Board', to: `/league/${id}/draft` }} />
 */

import { Link } from 'react-router-dom'
import { V2 } from './JaiV2'
import { JAI_FONTS } from './JaiBrand'
import { IS_NATIVE_APP } from '../services/runtime'

// Same overflow issue and same fix as ToolHeader.jsx's NavPill — see there.
// `parent` (a second pill, e.g. "Go to Draft Board") is dropped entirely on
// native rather than given an icon too — two bare back-arrows side by side
// would say nothing about which one does what.
function NavPill({ to, children }) {
  if (IS_NATIVE_APP) {
    return (
      <Link to={to} aria-label={typeof children === 'string' ? children : 'Back'}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, borderRadius: '50%',
          background: 'white', border: `1px solid ${V2.hairSoft}`,
          color: V2.mute, textDecoration: 'none', flexShrink: 0,
        }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    )
  }
  return (
    <Link
      to={to}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontWeight: 600, color: V2.mute,
        background: 'white', border: `1px solid ${V2.hairSoft}`,
        borderRadius: 999, padding: '6px 13px',
        textDecoration: 'none', fontFamily: JAI_FONTS.tight,
        transition: 'border-color 150ms, color 150ms',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(30,91,214,0.3)'; e.currentTarget.style.color = V2.blue }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = V2.hairSoft; e.currentTarget.style.color = V2.mute }}
    >
      {children}
    </Link>
  )
}

export default function ToolNav({ leagueId, parent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      <NavPill to={`/league/${leagueId}`}>Go to League Home Screen</NavPill>
      {/* Dropped on native — same reasoning as ToolHeader.jsx's NavPill. */}
      {parent && !IS_NATIVE_APP && <NavPill to={parent.to}>{parent.label}</NavPill>}
    </div>
  )
}
